'use client';

/**
 * ObeyaSnapshotCard — procurement snapshot (RFQs sent/draft, vendor
 * responses, quote range, per-vendor totals).
 *
 * Mode1-native: no `<ProcurementSnapshot />` import from the legacy
 * obeya widget. Same data source (`useObeyaProjectData` →
 * `obeya.detail`); same paint shape; stateless leaf composition.
 *
 * @see klyde_edge/composition/cards/obeya.py — content_key="obeya_snapshot"
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { LayoutDashboard } from 'lucide-react';
import { useObeyaProjects } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens.x';
import { useObeyaProjectData } from './use-obeya-project-data';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

interface RAGNode {
  semantic_id?: string;
  properties?: Record<string, unknown> | null;
  vendor_responses?: VendorResponseSummary[];
}

interface VendorResponseSummary {
  semantic_id?: string;
  vendor_name?: string;
  total_amount?: number | null;
  rag_status?: string;
}

export function ObeyaSnapshotCard() {
  const { data, isLoading, projectId, queryKey } = useObeyaProjectData();
  const { contentKey } = useCardIdentity();
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  // Labels travel with the list strategy — same source the legacy
  // ProcurementSnapshot read for i18n.
  const { data: labelsData } = useObeyaProjects(listStrategy ?? '', {
    limit: 50,
    ...(lang ? { lang } : {}),
  });
  const L = labelsData?.labels ?? {};

  const stats = useMemo(() => {
    if (!data) return null;
    const rfqs = (data.rfqs ?? []) as RAGNode[];
    const vendors = (data.vendors ?? []) as RAGNode[];

    const activeStates = new Set(['sent', 'rfq_sent']);
    const activeRfqs = rfqs.filter((r) =>
      activeStates.has(r.properties?.state as string),
    );
    const rfqsSent = rfqs.filter((r) => {
      const state = r.properties?.state as string;
      return state === 'sent' || state === 'rfq_sent';
    }).length;
    const rfqsDraft = rfqs.filter(
      (r) => (r.properties?.state as string) === 'draft',
    ).length;

    const allResponses: VendorResponseSummary[] = activeRfqs.flatMap(
      (r) => r.vendor_responses ?? [],
    );
    const respondedVendors = new Set(
      allResponses.map((vr) => vr.vendor_name).filter(Boolean),
    );
    const totals = allResponses
      .filter((vr) => vr.total_amount && vr.total_amount > 0)
      .map((vr) => vr.total_amount as number);

    const minTotal = totals.length > 0 ? Math.min(...totals) : null;
    const maxTotal = totals.length > 0 ? Math.max(...totals) : null;

    // Dedupe by vendor; prefer non-revised.
    const byVendor = new Map<string, VendorResponseSummary>();
    for (const vr of allResponses) {
      const key = vr.vendor_name || vr.semantic_id || '';
      const existing = byVendor.get(key);
      if (!existing || vr.rag_status !== 'revised') byVendor.set(key, vr);
    }

    return {
      rfqCount: rfqs.length,
      rfqsSent,
      rfqsDraft,
      vendorsInvited: vendors.length,
      vendorsResponded: respondedVendors.size,
      responseCount: allResponses.length,
      responses: [...byVendor.values()],
      minTotal,
      maxTotal,
    };
  }, [data]);

  // Scene export — register the snapshot rail card into the capture envelope.
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_snapshot',
    status: (data ? 'loaded' : projectId ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: projectId ? queryKey : null,
    text: stats
      ? `${stats.rfqCount} RFQs · ${stats.vendorsResponded}/${stats.vendorsInvited} vendors responded`
      : 'Snapshot',
    json: stats
      ? {
          project_id: projectId ?? null,
          rfq_count: stats.rfqCount,
          rfqs_sent: stats.rfqsSent,
          vendors_invited: stats.vendorsInvited,
          vendors_responded: stats.vendorsResponded,
          response_count: stats.responseCount,
        }
      : { project_id: projectId ?? null },
  }), [contentKey, data, projectId, queryKey, stats]));

  if (!projectId) return <Shell>Select a project</Shell>;
  if (isLoading || !data || !stats) return <Shell>Loading…</Shell>;

  const fmt = (n: number) =>
    `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden" style={{ fontSize: surfaceBase.rail }}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <LayoutDashboard className="w-3.5 h-3.5 text-violet-400" />
        <span className={text.heading}>
          {L.procurement_snapshot ?? 'Procurement Snapshot'}
        </span>
      </div>
      <div className="p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className={text.muted}>
            {L.rfqs_issued ?? 'RFQs issued'}
          </span>
          <span className={text.title}>
            {stats.rfqsSent} sent
            {stats.rfqsDraft > 0 ? `, ${stats.rfqsDraft} draft` : ''}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className={text.muted}>
            {L.vendor_responses ?? 'Vendor responses'}
          </span>
          <span className={text.title}>
            {stats.vendorsResponded} of {stats.vendorsInvited} vendors
            {stats.vendorsInvited > 0 && (
              <span
                className={cn(
                  'ml-1',
                  stats.vendorsResponded === stats.vendorsInvited
                    ? 'text-green-400'
                    : stats.vendorsResponded === 0
                      ? 'text-muted-foreground'
                      : 'text-amber-400',
                )}
              >
                ({Math.round((stats.vendorsResponded / stats.vendorsInvited) * 100)}%)
              </span>
            )}
          </span>
        </div>

        {stats.minTotal !== null && stats.maxTotal !== null && (
          <div className="flex items-center justify-between">
            <span className={text.muted}>
              {L.quote_range ?? 'Quote range'}
            </span>
            <span className={text.title}>
              {stats.minTotal === stats.maxTotal
                ? fmt(stats.minTotal)
                : `${fmt(stats.minTotal)} — ${fmt(stats.maxTotal)}`}
              {stats.maxTotal > 0 && stats.minTotal < stats.maxTotal && (
                <span className={cn(text.muted, 'ml-1')}>
                  ({Math.round(((stats.maxTotal - stats.minTotal) / stats.maxTotal) * 100)}%{' '}
                  {L.spread ?? 'spread'})
                </span>
              )}
            </span>
          </div>
        )}

        {stats.responses.length > 0 && (
          <div className="pt-1 border-t border-border/50 space-y-1">
            {stats.responses.map((vr) => (
              <div
                key={vr.vendor_name ?? vr.semantic_id}
                className="flex items-center justify-between"
              >
                <span className={cn(text.body, 'truncate max-w-40')}>
                  {vr.vendor_name}
                </span>
                <span className={cn(text.title, 'tabular-nums')}>
                  {vr.total_amount && vr.total_amount > 0 ? fmt(vr.total_amount) : '—'}
                </span>
              </div>
            ))}
          </div>
        )}

        {stats.responseCount === 0 && stats.rfqsSent > 0 && (
          <div className={cn(text.muted, 'italic pt-1')}>
            {L.awaiting_quotes ?? 'Awaiting quotes…'}
          </div>
        )}
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <article className="rounded-md border border-border bg-card shadow-sm p-3" style={{ fontSize: surfaceBase.rail }}>
      <h3 className={cn(text.heading, 'mb-2')}>
        Procurement Snapshot
      </h3>
      <div className={cn(text.muted, 'py-2')}>{children}</div>
    </article>
  );
}
